// Tags Context for tag management
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { 
    getTags, 
    getTag, 
    createTag as createTagFirestore, 
    updateTag as updateTagFirestore, 
    deleteTag as deleteTagFirestore 
} from '../services/firestore.js';
import { useAuth } from './AuthContext.jsx';

const TagsContext = createContext(null);

export function TagsProvider({ children }) {
    const { user } = useAuth();
    
    // State
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTagIds, setSelectedTagIds] = useState([]); // For filtering notes

    // Load tags from Firestore
    const loadTags = useCallback(async () => {
        if (!user) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const userTags = await getTags(user.uid);
            setTags(userTags);
        } catch (err) {
            console.error('Failed to load tags:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Load tags when user changes
    useEffect(() => {
        if (user) {
            loadTags();
        } else {
            setTags([]);
            setLoading(false);
            setSelectedTagIds([]);
        }
    }, [user, loadTags]);

    // Create a new tag
    const createTag = useCallback(async (tagData) => {
        if (!user) return null;
        
        setError(null);
        
        try {
            const newTag = await createTagFirestore(user.uid, tagData);
            // Add to local state
            setTags(prev => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
            return newTag;
        } catch (err) {
            console.error('Failed to create tag:', err);
            setError(err.message);
            throw err;
        }
    }, [user]);

    // Update an existing tag
    const updateTag = useCallback(async (tagId, updates) => {
        if (!user) return;
        
        setError(null);
        
        try {
            await updateTagFirestore(user.uid, tagId, updates);
            // Update local state
            setTags(prev => prev.map(tag => 
                tag.id === tagId 
                    ? { ...tag, ...updates, updatedAt: new Date() }
                    : tag
            ).sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) {
            console.error('Failed to update tag:', err);
            setError(err.message);
            throw err;
        }
    }, [user]);

    // Delete a tag
    const deleteTag = useCallback(async (tagId) => {
        if (!user) return null;
        
        setError(null);
        
        try {
            const result = await deleteTagFirestore(user.uid, tagId);
            // Remove from local state
            setTags(prev => prev.filter(tag => tag.id !== tagId));
            // Remove from selected tags if present
            setSelectedTagIds(prev => prev.filter(id => id !== tagId));
            return result;
        } catch (err) {
            console.error('Failed to delete tag:', err);
            setError(err.message);
            throw err;
        }
    }, [user]);

    // Toggle tag in filter selection
    const toggleTagFilter = useCallback((tagId) => {
        setSelectedTagIds(prev => {
            if (prev.includes(tagId)) {
                return prev.filter(id => id !== tagId);
            }
            return [...prev, tagId];
        });
    }, []);

    // Clear all tag filters
    const clearTagFilters = useCallback(() => {
        setSelectedTagIds([]);
    }, []);

    // Get tag by ID from local state
    const getTagById = useCallback((tagId) => {
        return tags.find(tag => tag.id === tagId) || null;
    }, [tags]);

    // Get multiple tags by IDs
    const getTagsByIds = useCallback((tagIds) => {
        if (!tagIds || !Array.isArray(tagIds)) return [];
        return tags.filter(tag => tagIds.includes(tag.id));
    }, [tags]);

    // Memoized selected tags
    const selectedTags = useMemo(() => {
        return getTagsByIds(selectedTagIds);
    }, [selectedTagIds, getTagsByIds]);

    // Check if a tag name already exists
    const tagExists = useCallback((name) => {
        const normalizedName = name.toLowerCase().trim();
        return tags.some(tag => tag.name === normalizedName);
    }, [tags]);

    // Find or create tag by name
    const findOrCreateTag = useCallback(async (name) => {
        const normalizedName = name.toLowerCase().trim();
        const existingTag = tags.find(tag => tag.name === normalizedName);
        
        if (existingTag) {
            return existingTag;
        }
        
        return await createTag({ name: normalizedName });
    }, [tags, createTag]);

    const value = {
        tags,
        loading,
        error,
        selectedTagIds,
        selectedTags,
        loadTags,
        createTag,
        updateTag,
        deleteTag,
        toggleTagFilter,
        clearTagFilters,
        getTagById,
        getTagsByIds,
        tagExists,
        findOrCreateTag
    };

    return (
        <TagsContext.Provider value={value}>
            {children}
        </TagsContext.Provider>
    );
}

export function useTags() {
    const context = useContext(TagsContext);
    if (!context) {
        throw new Error('useTags must be used within a TagsProvider');
    }
    return context;
}